import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import React, { useEffect, useState } from "react";

type Easing = "ease-in" | "ease-out" | "ease-in-out" | "linear";

interface CircleProgressProps {
  /** Progress (0-1) for the main (inner) circle */
  value: number;
  /** Optional secondary progress (0-1) for an outer/thicker circle */
  secondaryValue?: number;
  /** Color for the main (inner) circle */
  color?: string;
  /** Color for the secondary (outer) circle */
  secondaryColor?: string;
  /** Color for the secondary circle in a highlighted state */
  secondaryHighlightColor?: string;
  /** State for the secondary circle: 'default' | 'highlight' */
  secondaryState?: "default" | "highlight";
  /** How much of the circle to show (0-1), e.g. 0.75 for 3/4 circle */
  circleCompletion?: number;
  /** Optionally render children inside the circle */
  children?: React.ReactNode;
  /** Optional rotation in degrees (clockwise from default position) */
  rotation?: number;
  /** Optional gradient colors */
  gradientColors?: [string, string];
  /** Should have animation (default is no animation). Note these animation
   * configuration values are separated to avoid the potential issue of
   * re-renders when passing a non-memoized configuration object in as props */
  animationEnabled?: boolean;
  /** Optional animation duration (default is 1500ms) */
  animationDuration?: number;
  /** Optional animation delay (default is 0ms) */
  animationDelay?: number;
  /** Optional animation easing (default is "ease-out") */
  animationEasing?: Easing;
  /** Optional animation direction (default is "forward") */
  animationDirection?: "forward" | "backward";
}

const INNER_STROKE_WIDTH = 15; // Main progress circle
const SECONDARY_STROKE_WIDTH = 10; // Secondary (inner) circle
const RADIUS = 110;
const SECONDARY_RADIUS = 92;
const SIZE = 250;

function easeProgress(progress: number, easing: Easing): number {
  // eslint-disable-next-line default-case
  switch (easing) {
    case "ease-in": {
      return progress * progress;
    }

    case "ease-out": {
      return 1 - (1 - progress) * (1 - progress);
    }

    case "ease-in-out": {
      return progress < 0.5 ? 2 * progress * progress : 1 - 2 * (1 - progress) * (1 - progress);
    }

    case "linear": {
      return progress;
    }
  }
}

export function CircleProgress(props: CircleProgressProps) {
  const theme = useTheme();

  const {
    value,
    secondaryValue,
    secondaryState = "default",
    circleCompletion = 1,
    children,
    rotation = 0,
    color = theme.palette.components?.circleProgress?.primary,
    secondaryColor = theme.palette.components?.circleProgress?.secondary,
    secondaryHighlightColor = theme.palette.components?.circleProgress?.warning,
    gradientColors,
    animationEnabled = false,
    animationDuration = 1500,
    animationDelay = 0,
    animationEasing = "ease-out",
    animationDirection = "forward",
  } = props;

  const [animatedValue, setAnimatedValue] = useState(0);

  // Clamp progress values
  const mainProgress = Math.max(0, Math.min(value, 1));
  const secondaryProgress =
    secondaryValue === undefined ? undefined : Math.max(0, Math.min(secondaryValue, 1));

  const clampedCircleCompletion = Math.max(0, Math.min(circleCompletion, 1));

  const mainProgressDisplay = animationEnabled ? animatedValue : mainProgress;

  useEffect(() => {
    if (!animationEnabled) {
      setAnimatedValue(mainProgress);
      return;
    }

    const startValue = animationDirection === "backward" ? 1 : 0;

    const timer = setTimeout(() => {
      setAnimatedValue(startValue);

      const startTime = Date.now();
      const animateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        const easedProgress = easeProgress(progress, animationEasing);

        if (animationDirection === "backward") {
          setAnimatedValue(startValue + (mainProgress - startValue) * easedProgress);
        } else {
          setAnimatedValue(mainProgress * easedProgress);
        }

        if (progress < 1) {
          requestAnimationFrame(animateProgress);
        }
      };

      requestAnimationFrame(animateProgress);
    }, animationDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [
    mainProgress,
    animationEnabled,
    animationDuration,
    animationDelay,
    animationEasing,
    animationDirection,
  ]);

  // Geometry
  const fullCircumference = 2 * Math.PI * RADIUS;
  const circumference = fullCircumference * clampedCircleCompletion;
  const mainOffset = circumference * (1 - mainProgressDisplay);

  const secondaryCircumference = 2 * Math.PI * SECONDARY_RADIUS;
  const secOffset =
    secondaryProgress === undefined ? 0 : secondaryCircumference * (1 - secondaryProgress);

  // Always start from top (-90 deg), partial circles will be clipped by strokeDasharray
  const baseRotation = -90;
  const totalRotation = baseRotation + rotation;

  // Secondary color logic
  const secStroke = secondaryState === "highlight" ? secondaryHighlightColor : secondaryColor;
  const gradientId = "progressGradient";
  const gradientUrl = gradientColors ? `url(#${gradientId})` : undefined;

  return (
    <Box position="relative" width={SIZE} height={SIZE} display="inline-block">
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={{ transform: `rotate(${totalRotation}deg)` }}
      >
        {/* Gradient if provided */}
        {gradientColors && (
          <defs>
            <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={gradientColors[0]} />
              <stop offset="100%" stopColor={gradientColors[1]} />
            </linearGradient>
          </defs>
        )}
        {/* Background track for main circle (should be first) */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={theme.palette.components?.circleProgress?.track}
          strokeWidth={INNER_STROKE_WIDTH}
          strokeDasharray={`${circumference} ${fullCircumference}`}
          strokeDashoffset={0}
          style={{ position: "absolute", zIndex: 0, opacity: 0.1 }}
          strokeLinecap="round"
        />
        {/* Main Progress Circle */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={gradientUrl ?? color}
          strokeWidth={INNER_STROKE_WIDTH}
          strokeDasharray={`${circumference} ${fullCircumference}`}
          strokeDashoffset={mainOffset}
          strokeLinecap="round"
          {...(!animationEnabled && {
            style: { transition: "stroke-dashoffset 0.5s" },
          })}
        />
        {/* Secondary Inner Circle */}
        {secondaryProgress !== undefined && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={SECONDARY_RADIUS}
            fill="none"
            stroke={secStroke}
            strokeWidth={SECONDARY_STROKE_WIDTH}
            strokeDasharray={`${secondaryCircumference} ${secondaryCircumference}`}
            strokeDashoffset={secOffset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.5s, opacity 0.5s" }}
          />
        )}
      </svg>
      {/* Children (centered content) */}
      {children && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width={SIZE}
          height={SIZE}
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ pointerEvents: "none" }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

export default CircleProgress;
